import { useMemo } from 'react';
import { useTransactionStore } from '@/store/transactionsStore';
import { formatCurrency } from '@/utils/formatters';
import { FileText, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { useAccountsStore } from '@/store/accountsStore';
import { useTagsStore } from '@/store/tagsStore';
import TransactionActions from './TransactionActions';
import { cn } from '@/lib/utils';
import { Badge } from '../ui/badge';
import { format } from 'date-fns';

interface TransactionGridProps {
  accountId?: string;
}

export default function TransactionGrid({ accountId }: TransactionGridProps) {
  const { transactions } = useTransactionStore();
  // @ts-ignore - Type conversion issues during migration
  const { accounts } = useAccountsStore();
  const { tags } = useTagsStore();
  
  // Filter transactions for the specific account if accountId is provided
  const displayTransactions = useMemo(() => {
    if (!accountId) return transactions;
    
    // @ts-ignore - Type compatibility issues during migration
    return transactions.filter((transaction: any) => {
      // Check if it's a regular transaction for this account
      if (transaction.account_id === accountId) return true;
      
      // Check if it's a transfer from this account
      if (transaction.transfer_details?.from_account_id === accountId) return true;
      
      // Check if it's a transfer to this account
      if (transaction.transfer_details?.to_account_id === accountId) return true;
      
      return false;
    });
  }, [transactions, accountId]);

  const formatDate = (date: Date | string) => {
    try {
      // Handle string dates
      if (typeof date === 'string') {
        return format(new Date(date), 'MMM d, yyyy');
      }
      // Handle Date objects
      return format(date, 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const getAccountName = (accountId: string) => {
    const account = accounts.find((a: any) => a.id === accountId);
    return account ? account.name : 'Unknown Account';
  };

  const getTransactionTags = (transaction: any) => {
    if (!transaction.tags || transaction.tags.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-1 mt-1">
        {transaction.tags.map((tagId: string) => {
          const tag = tags.find((tag: any) => tag.id === tagId);
          if (!tag) return null;
          
          return (
            <Badge key={tagId} variant="outline" className="text-xs">
              {tag.name}
            </Badge>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {displayTransactions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No transactions found
        </div>
      ) : (
        // @ts-ignore - Type compatibility issues during migration
        displayTransactions.map((transaction: any) => (
          <div
            key={transaction.id}
            className="flex items-start justify-between p-4 rounded-lg border"
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                {transaction.transfer_details ? (
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <ArrowUpRight className="h-5 w-5 text-blue-600" />
                  </div>
                ) : transaction.amount < 0 ? (
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <ArrowUpRight className="h-5 w-5 text-red-600" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <ArrowDownRight className="h-5 w-5 text-green-600" />
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-medium">
                  {transaction.description}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {formatDate(transaction.date)}
                </p>
                {transaction.category_id && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {transaction.category_id}
                  </p>
                )}
                {getTransactionTags(transaction)}
              </div>
            </div>
            <div className="flex flex-col items-end">
              <p
                className={cn(
                  "font-medium",
                  transaction.amount < 0
                    ? "text-red-600 dark:text-red-500"
                    : "text-green-600 dark:text-green-500"
                )}
              >
                {formatCurrency(transaction.amount)}
              </p>
              {transaction.transfer_details && (
                <p className="text-xs text-muted-foreground">
                  Transfer: {getAccountName(transaction.transfer_details.from_account_id)} →{" "}
                  {getAccountName(transaction.transfer_details.to_account_id)}
                </p>
              )}
              <div className="flex items-center mt-2">
                {transaction.has_attachment && (
                  <FileText className="h-4 w-4 text-muted-foreground mr-2" />
                )}
                <TransactionActions transactionId={transaction.id} />
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
